import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { AppContext } from '../../context/AppContext';
import { sortRacesByName } from '../../utils/sortRacesByName';
import { FaTrophy, FaUsers, FaUser, FaGamepad, FaCalculator, FaFlag, FaEye } from '../../components/Icons';
import { SectionHeaderComp } from '../ligas/LastresLigaPage';
import { theme } from '../../theme';
import useResponsive from '../../hooks/useResponsive';

// Animación para el brillo de las tarjetas
const shine = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(228, 49, 46, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(228, 49, 46, 0); }
  100% { box-shadow: 0 0 0 0 rgba(228, 49, 46, 0); }
`;

const Container = styled.div`
  width: 100%;
  padding: 20px 0;
  background: ${theme.colors.background.default};
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 8px 0;
  }
`;

const Title = styled.h1`
  color: ${theme.colors.primary.main};
  font-size: 2.2rem;
  text-align: center;
  margin: 0 0 30px 0;
  padding: 20px 0;
  background: ${theme.colors.background.paper};
  border-bottom: 1px solid ${theme.colors.divider};
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 1.8rem;
    margin: 0 0 20px 0;
    padding: 15px 10px;
  }
`;

const CardsContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  
  @media (max-width: 768px) {
    padding: 0 8px;
    gap: 12px;
    margin: 0;
    max-width: 100%;
  }
`;

const LigaCard = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: 8px;
  padding: 20px;
  color: ${theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: all 0.2s ease;
  border: 1px solid ${theme.colors.divider};
  
  &:hover {
    border-color: ${theme.colors.primary.main};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 15px;
    border-radius: 6px;
  }
`;

const LigaIcon = styled.div`
  font-size: 2rem;
  color: ${theme.colors.secondary.main};
  margin-bottom: 10px;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${theme.colors.background.default};
  border: 1px solid ${theme.colors.divider};
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    width: 50px;
    height: 50px;
    margin-bottom: 8px;
  }
`;

const LigaName = styled.h2`
  color: ${theme.colors.primary.main};
  font-size: 1.6rem;
  margin: 5px 0;
  text-align: center;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin: 3px 0;
  }
`;

const LigaDescription = styled.p`
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin: 5px 0 15px;
  font-size: 0.95rem;
  line-height: 1.5;
  max-width: 80%;
  
  @media (max-width: 768px) {
    font-size: 0.85rem;
    margin: 3px 0 12px;
    max-width: 95%;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  width: 100%;
  margin: 10px 0 15px;
  padding: 12px 0;
  border-top: 1px solid ${theme.colors.divider};
  border-bottom: 1px solid ${theme.colors.divider};
  
  @media (max-width: 768px) {
    margin: 8px 0 12px;
    padding: 10px 0;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 15px;
  position: relative;
  
  &:not(:last-child)::after {
    content: '';
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    height: 60%;
    width: 1px;
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 0 8px;
    flex: 1;
    min-width: 80px;
    
    &:not(:last-child)::after {
      display: none;
    }
  }
`;

const StatValue = styled.div`
  font-size: 1.3rem;
  font-weight: 600;
  color: ${theme.colors.secondary.main};
  margin-bottom: 2px;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 10px;
  width: 100%;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 6px;
    margin-top: 8px;
  }
`;

const ClassificationButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  background: transparent;
  color: ${theme.colors.primary.main};
  border-radius: 4px;
  gap: 6px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s ease;
  border: 1px solid ${theme.colors.primary.main};
  font-size: 0.85rem;
  min-width: 120px;
  text-align: center;
  
  &:hover {
    background: ${theme.colors.primary.main};
    color: white;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    font-size: 0.9rem;
  }
  
  &.team, &.gamer, &.lastre {
    border-color: ${theme.colors.primary.main};
    color: ${theme.colors.primary.main};
    
    &:hover {
      background: ${theme.colors.primary.main};
      color: white;
    }
  }
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    font-size: 0.8rem;
    min-width: auto;
    width: 100%;
    
    svg {
      font-size: 0.85rem;
    }
  }
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 30px 20px;
  background: ${theme.colors.background.paper};
  border-radius: 8px;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.divider};
  margin: 20px 0;
  
  p {
    margin: 10px 0 0;
    font-size: 1rem;
    color: ${theme.colors.text.secondary};
  }
`;

const ClasificacionesList: React.FC = () => {
  const { data } = useContext(AppContext);
  const { isMobile } = useResponsive();

  // Función para contar pilotos por liga
  const contarPilotosPorLiga = (ligaId: string) => {
    // Obtener la liga para comparar su categoría con la categoría de los socios
    const liga = data.ligas.find(l => l.id === ligaId);
    if (!liga) return 0;
    
    // Verificar si la liga tiene una categoría definida
    const ligaCategoria = liga.categoria || '';
    if (!ligaCategoria) {
      console.log(`Liga ${liga.nombre} no tiene categoría definida`);
      return 0;
    }
    
    // Filtrar socios por Categoria
    const sociosFiltrados = data.socios.filter(socio => {
      const socioCategoria = (socio.Categoria || '').trim().toUpperCase();
      const ligaCategoriaUpper = ligaCategoria.trim().toUpperCase();
      return socioCategoria === ligaCategoriaUpper;
    });
    
    console.log(`Liga ${liga.nombre} (categoría: ${ligaCategoria}) tiene ${sociosFiltrados.length} pilotos`);
    return sociosFiltrados.length;
  };
  
  // Función para contar carreras por liga
  const contarCarrerasPorLiga = (ligaId: string) => {
    return data.carreras.filter((carrera: any) => carrera.ligaId === ligaId).length;
  };

  return (
    <Container>
      <SectionHeaderComp icon={<FaTrophy style={{ color: '#e4312e' }} />}>
        Clasificaciones
      </SectionHeaderComp>
      
      {data.ligas.length > 0 ? (
        <CardsContainer>
          {data.ligas.map(liga => (
            <LigaCard key={liga.id}>
              <LigaIcon>
                <FaTrophy />
              </LigaIcon>
              <LigaName>{liga.nombre}</LigaName>
              {liga.descripcion && (
                <LigaDescription>{liga.descripcion}</LigaDescription>
              )}
              
              <StatsContainer>
                <StatItem>
                  <StatValue>
                    {contarPilotosPorLiga(liga.id)}
                    <FaUser style={{ marginLeft: '5px', fontSize: '0.9rem', opacity: 0.8 }} />
                  </StatValue>
                  <StatLabel>Pilotos</StatLabel>
                </StatItem>
                
                <StatItem>
                  <StatValue>
                    {contarCarrerasPorLiga(liga.id)}
                    <FaFlag style={{ marginLeft: '5px', fontSize: '0.9rem', opacity: 0.8 }} />
                  </StatValue>
                  <StatLabel>Carreras</StatLabel>
                </StatItem>
                
                <StatItem>
                  <StatValue>
                    {contarPilotosPorLiga(liga.id) > 0 ? 'Activa' : 'Inactiva'}
                    <FaTrophy style={{ marginLeft: '5px', fontSize: '0.9rem', 
                      color: contarPilotosPorLiga(liga.id) > 0 ? '#FFD700' : '#888',
                      opacity: 0.9 
                    }} />
                  </StatValue>
                  <StatLabel>Estado</StatLabel>
                </StatItem>
              </StatsContainer>
              
              <ButtonsContainer>
                <ClassificationButton to={`/clasificacion-gamer/${liga.id}`}>
                  <FaUser /> Individual
                </ClassificationButton>
                <ClassificationButton to={`/clasificaciones/equipos/${liga.id}`} className="team">
                  <FaUsers /> Equipos
                </ClassificationButton>
                <ClassificationButton to={`/clasificaciones/${liga.id}`}>
                  <FaEye /> Detalle
                </ClassificationButton>
                <ClassificationButton to={`/ligas/${liga.id}/lastres`} className="lastre">
                  <FaCalculator /> Lastres
                </ClassificationButton>
              </ButtonsContainer>
            </LigaCard>
          ))}
        </CardsContainer>
      ) : (
        <EmptyMessage>
          <p>No hay ligas registradas.</p>
          <p>Crea una liga para ver su clasificación.</p>
        </EmptyMessage>
      )}
    </Container>
  );
};

export default ClasificacionesList;
